import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ page = 1, limit = 20, unreadOnly = false, append = false } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/notifications", { params: { page, limit, unreadOnly } });
      return { ...res.data, append, page };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch notifications");
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/notifications/unread-count");
      return res.data.unreadCount;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch unread count");
    }
  }
);

export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/notifications/${id}/read`);
      return res.data.notification;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      await api.patch("/notifications/mark-all-read");
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

export const markBulkRead = createAsyncThunk(
  "notifications/markBulkRead",
  async (ids, { rejectWithValue }) => {
    try {
      await api.patch("/notifications/mark-bulk-read", { ids });
      return ids;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/notifications/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

export const deleteBulkNotifications = createAsyncThunk(
  "notifications/deleteBulkNotifications",
  async (ids, { rejectWithValue }) => {
    try {
      await api.delete("/notifications/bulk", { data: { ids } });
      return ids;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

export const clearAllNotifications = createAsyncThunk(
  "notifications/clearAllNotifications",
  async (_, { rejectWithValue }) => {
    try {
      await api.delete("/notifications/clear-all");
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const notificationSlice = createSlice({
  name: "notifications",
  initialState: {
    items: [],
    total: 0,
    unreadCount: 0,
    page: 1,
    totalPages: 1,
    hasMore: false,
    loading: false,
    loadingMore: false,
    error: null,
  },
  reducers: {
    // Called when a real-time push notification arrives (e.g. via WebSocket/socket.io)
    pushNotification: (state, action) => {
      state.items.unshift(action.payload);
      state.total += 1;
      state.unreadCount += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNotifications
      .addCase(fetchNotifications.pending, (state, action) => {
        if (action.meta.arg?.append) state.loadingMore = true;
        else state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        const incoming = action.payload.notifications || [];
        if (action.payload.append) {
          state.items = [...state.items, ...incoming];
        } else {
          state.items = incoming;
        }
        state.total = action.payload.total;
        state.unreadCount = action.payload.unreadCount;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })

      // fetchUnreadCount
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })

      // markAsRead
      .addCase(markAsRead.fulfilled, (state, action) => {
        const idx = state?.items?.findIndex((n) => n._id === action.payload._id);
        if (idx !== -1) {
          if (!state.items[idx].isRead) state.unreadCount = Math.max(0, state.unreadCount - 1);
          state.items[idx] = action.payload;
        }
      })

      // markAllAsRead
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.items = state.items.map((n) => ({ ...n, isRead: true }));
        state.unreadCount = 0;
      })

      // markBulkRead
      .addCase(markBulkRead.fulfilled, (state, action) => {
        const ids = new Set(action.payload);
        let reduced = 0;
        state.items = state.items.map((n) => {
          if (ids.has(n._id) && !n.isRead) { reduced++; return { ...n, isRead: true }; }
          return n;
        });
        state.unreadCount = Math.max(0, state.unreadCount - reduced);
      })

      // deleteNotification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const removed = state.items.find((n) => n._id === action.payload);
        state.items = state.items.filter((n) => n._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (removed && !removed.isRead) state.unreadCount = Math.max(0, state.unreadCount - 1);
      })

      // deleteBulkNotifications
      .addCase(deleteBulkNotifications.fulfilled, (state, action) => {
        const ids = new Set(action.payload);
        const removed = state.items.filter((n) => ids.has(n._id));
        const unreadRemoved = removed.filter((n) => !n.isRead).length;
        state.items = state.items.filter((n) => !ids.has(n._id));
        state.total = Math.max(0, state.total - removed.length);
        state.unreadCount = Math.max(0, state.unreadCount - unreadRemoved);
      })

      // clearAllNotifications
      .addCase(clearAllNotifications.fulfilled, (state) => {
        state.items = [];
        state.total = 0;
        state.unreadCount = 0;
        state.page = 1;
        state.totalPages = 1;
        state.hasMore = false;
      });
  },
});

export const { pushNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
